// Mirrors the request contract of the devspace-builds-upload edge function
// (backend/supabase/functions/devspace-builds-upload/index.ts: UploadSchema,
// BUILD_METADATA_HEADER, readUploadMetadata). The dashboard cannot import Deno
// edge-function code, so this is a literal copy and the two must stay in sync.
//
// The upload body is the raw zip rather than multipart form data: the edge
// function streams the body straight to ephemeral storage, and parsing
// multipart would mean buffering the whole build in an isolate capped at
// 256 MB. The metadata therefore rides in a header instead.

/** Header carrying the upload's metadata as base64-encoded JSON. */
export const BUILD_METADATA_HEADER = "x-build-meta";

/**
 * Mirrors MAX_METADATA_HEADER_BYTES in the edge function.
 *
 * The constraint is the gateway's per-header byte limit, so the encoded value
 * is what gets measured — not a character count. Notes are arbitrary UTF-8, so
 * one character may be several bytes, and base64 adds a further 4/3 on top.
 * Exceeding it fails at the edge before the function runs.
 */
export const MAX_BUILD_METADATA_HEADER_BYTES = 4096;

export interface BuildUploadMetadata {
  game_key: string;
  platform: "unity" | "js";
  notes?: string;
  build_id?: number;
  zip_file_name: string;
}

/**
 * Encodes metadata for the header. Base64 of the UTF-8 bytes rather than
 * `btoa(json)` directly, because header values are byte-oriented and notes may
 * contain any UTF-8 character, which `btoa` would throw on.
 */
export function encodeBuildUploadMetadata(metadata: BuildUploadMetadata): string {
  const bytes = new TextEncoder().encode(JSON.stringify(metadata));

  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  const encoded = btoa(binary);

  if (encoded.length > MAX_BUILD_METADATA_HEADER_BYTES) {
    throw new Error("Build notes are too long. Please shorten them and try again.");
  }

  return encoded;
}
